// import { Lunar } from 'lunar-javascript';
import QRCode from 'qrcode';
import { getImageUrl } from '@/utils';
import type { AnswerbookItem } from '@/types/question';

const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] => {
  let line = '';
  let lines = [];
  const words = text.split('');

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i];
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth) {
      lines.push(line);
      line = words[i];
    } else {
      line = testLine;
    }
  }
  lines.push(line);

  // 如果超过最大行数，在最后一行添加省略号
  if (lines.length > maxLines) {
    const lastLine = lines[maxLines - 1];
    let truncatedLine = lastLine;
    while (ctx.measureText(truncatedLine + '...').width > maxWidth) {
      truncatedLine = truncatedLine.slice(0, -1);
    }
    lines = lines.slice(0, maxLines - 1).concat([truncatedLine + '...']);
  }

  return lines;
};

// 添加一个通用的图片加载函数
const loadImage = (src: string, useCrossOrigin = true): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCrossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const loadImageWithFallback = async (urls: string[]) => {
  for (const url of urls) {
    if (!url) continue;
    try {
      return await loadImage(url, true);
    } catch (error) {
      console.error('圖片加載失敗:', url, error);
      try {
        return await loadImage(url, false);
      } catch (fallbackError) {
        console.error('圖片加載重試失敗:', url, fallbackError);
      }
    }
  }
  return undefined;
};

const drawCoverImage = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number
) => {
  const sourceRatio = image.width / image.height;
  const targetRatio = width / height;
  let sx = 0;
  let sy = 0;
  let sw = image.width;
  let sh = image.height;
  if (sourceRatio > targetRatio) {
    sw = image.height * targetRatio;
    sx = (image.width - sw) / 2;
  } else {
    sh = image.width / targetRatio;
    sy = (image.height - sh) / 2;
  }
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
};

const drawQuestionBar = async (
  ctx: CanvasRenderingContext2D,
  text: string,
  width: number,
  y: number
) => {
  const barWidth = width * 0.9;
  const barHeight = 38;
  const barX = (width - barWidth) / 2;

  const grad = ctx.createLinearGradient(barX, y, barX + barWidth, y);
  grad.addColorStop(0, 'rgba(82, 77, 71, 0.13)');
  grad.addColorStop(0.2, 'rgba(184, 174, 160, 0.65)');
  grad.addColorStop(0.8, 'rgba(184, 174, 160, 0.65)');
  grad.addColorStop(1, 'rgba(82, 77, 71, 0.13)');
  ctx.save();
  ctx.filter = 'blur(2.5px)';
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(barX, y, barWidth, barHeight, 20);
  ctx.fill();
  ctx.restore();

  const borderGradient = ctx.createLinearGradient(barX, y, barX + barWidth, y);
  borderGradient.addColorStop(0, 'rgba(254, 253, 248, 0.3)');
  borderGradient.addColorStop(0.22, 'rgba(254, 253, 248, 0.95)');
  borderGradient.addColorStop(0.78, 'rgba(254, 253, 248, 0.95)');
  borderGradient.addColorStop(1, 'rgba(254, 253, 248, 0.3)');

  ctx.save();
  ctx.filter = 'blur(0.75px)';
  ctx.strokeStyle = borderGradient;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(barX, y, barWidth, barHeight, 20);
  ctx.stroke();
  ctx.restore();

  const innerBorderGradient = ctx.createLinearGradient(barX + 6, y, barX + barWidth - 6, y);
  innerBorderGradient.addColorStop(0, 'rgba(254, 253, 248, 0.22)');
  innerBorderGradient.addColorStop(0.22, 'rgba(254, 253, 248, 0.85)');
  innerBorderGradient.addColorStop(0.78, 'rgba(254, 253, 248, 0.85)');
  innerBorderGradient.addColorStop(1, 'rgba(254, 253, 248, 0.22)');

  ctx.save();
  ctx.filter = 'blur(0.75px)';
  ctx.strokeStyle = innerBorderGradient;
  ctx.beginPath();
  ctx.roundRect(barX + 6, y, barWidth - 12, barHeight, 20);
  ctx.stroke();
  ctx.restore();

  const block = await loadImageWithFallback([getImageUrl('ans_button_block.png')]);
  if (block) {
    const blockW = 27;
    const blockH = 9;
    const blockY = y + 14.5;
    ctx.drawImage(block, barX, blockY, blockW, blockH);
    ctx.save();
    ctx.translate(barX + barWidth, blockY + blockH);
    ctx.rotate(Math.PI);
    ctx.drawImage(block, 0, 0, blockW, blockH);
    ctx.restore();
  } else {
    const decoGradient = ctx.createLinearGradient(barX, y, barX + 27, y);
    decoGradient.addColorStop(0, 'rgba(254, 253, 248, 0.15)');
    decoGradient.addColorStop(0.5, 'rgba(254, 253, 248, 0.9)');
    decoGradient.addColorStop(1, 'rgba(254, 253, 248, 0.15)');
    const decoY = y + 14.5;
    ctx.fillStyle = decoGradient;
    ctx.beginPath();
    ctx.roundRect(barX, decoY, 27, 9, 5);
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(barX + barWidth - 27, decoY, 27, 9, 5);
    ctx.fill();
  }

  ctx.fillStyle = '#FBF7E3';
  ctx.font = '900 14px XiangCuiKeSong';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(92, 85, 60, 0.31)';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 4;
  const questionLine = wrapText(ctx, text || '', barWidth - 70, 1)[0] || '';
  ctx.fillText(questionLine, width / 2, y + barHeight / 2);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
};

const drawPosterFooter = async (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  referralCode: string
) => {
  const sy = (value: number) => value * (height / 480);
  const footerHeight = sy(79);
  const footerY = height - footerHeight;
  const logoSize = sy(60);
  const logoX = sy(26);
  const logoY = footerY + (footerHeight - logoSize) / 2;
  const qrSize = sy(70);
  const qrX = width - qrSize - sy(22);
  const qrY = footerY + (footerHeight - qrSize) / 2;
  const textX = logoX + logoSize + sy(10);
  const gradient = ctx.createLinearGradient(0, footerY, 0, height);
  gradient.addColorStop(0, 'rgba(221, 182, 157, 0.6)');
  gradient.addColorStop(1, 'rgba(88, 56, 51, 0.6)');

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.roundRect(0, footerY, width, footerHeight, [10, 10, 0, 0]);
  ctx.fill();

  try {
    const logo = await loadImage(getImageUrl('mirror_logo.png'));
    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
  } catch (error) {
    console.error('Logo 加载失败:', error);
  }

  try {
    const qrUrl = `${window.location.origin}?referralCode=${referralCode}`;
    const dataUrl = await QRCode.toDataURL(qrUrl, {
      width: Math.round(qrSize),
      margin: 0,
      color: {
        dark: '#4f342c',
        light: '#efe2cf'
      },
      errorCorrectionLevel: 'H'
    });

    const qrImage = await loadImage(dataUrl);
    ctx.fillStyle = 'rgba(245, 231, 211, 0.32)';
    ctx.beginPath();
    ctx.roundRect(qrX, qrY, qrSize, qrSize, 10);
    ctx.fill();
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(qrX, qrY, qrSize, qrSize, 10);
    ctx.clip();
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

    const centerLogo = await loadImage(getImageUrl('mirror_logo.png'));
    const logoSize = sy(20);
    const logoX = qrX + (qrSize - logoSize) / 2;
    const logoY = qrY + (qrSize - logoSize) / 2;

    ctx.fillStyle = '#f6ecdc';
    ctx.beginPath();
    ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 + 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.drawImage(centerLogo, logoX, logoY, logoSize, logoSize);
    ctx.restore();
  } catch (error) {
    console.error('二维码生成或加载失败:', error);
  }

  ctx.font = '18px XiangCuiKeSong';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('答案之書', textX, footerY + footerHeight * 0.4);
  ctx.fillText('古詩詞版', textX, footerY + footerHeight * 0.72);
  ctx.textBaseline = 'alphabetic';
};

export const drawCanvas = async (
  canvasRef: HTMLCanvasElement,
  questionData: AnswerbookItem,
  referralCode: string,
  username: string
) => {
  const canvas = canvasRef;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }

  // 设置画布尺寸
  const dpr = window.devicePixelRatio || 1;
  const width =
    canvas.clientWidth || canvas.parentElement?.clientWidth || canvas.getBoundingClientRect().width;
  const designHeight = 480;
  const height = width * 1.8;
  const scaleY = height / designHeight;
  const sy = (value: number) => value * scaleY;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  // 绘制背景
  ctx.fillStyle = 'rgba(235, 211, 167, 0.85)';
  ctx.fillRect(0, 0, width, height);

  // 绘制用户名
  ctx.font = '16px XiangCuiKeSong';
  ctx.fillStyle = '#CA4821';
  ctx.fillText(`${username}`, 40, sy(46));

  // 绘制分享日期文本
  ctx.font = '13px AlibabaPuHuiTi';
  ctx.fillStyle = '#59372F';
  const currentDate = new Date();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  const currentDay = String(currentDate.getDate()).padStart(2, '0');
  const shareText = `${currentMonth}月${currentDay}日向您分享`;
  const usernameWidth = ctx.measureText(`用户${username}`).width;
  ctx.fillText(shareText, 60 + usernameWidth + 5, sy(46));

  // 绘制下方内容区域
  try {
    const insetImage = await loadImage(getImageUrl('share_inset.jpg'));
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(16, sy(62), width - 32, sy(399), [20, 20, 0, 0]);
    ctx.clip();
    ctx.drawImage(insetImage, 16, sy(62), width - 32, sy(399));
    ctx.restore();
  } catch (error) {
    console.error('内容区域背景图片加载失败:', error);
    // fallback 到原来的背景色
    ctx.fillStyle = 'rgba(255, 239, 216, 0.5)';
    ctx.beginPath();
    ctx.roundRect(16, sy(62), width - 32, sy(399), [20, 20, 0, 0]);
    ctx.fill();
  }

  // // 绘制用户提问信息
  // ctx.font = '15px AlibabaPuHuiTi';
  // ctx.fillStyle = '#CA4821';
  // const maxQuestionWidth = 162;
  // const lineHeight = 20;
  // const lines = wrapText(ctx, questionData.questionText, maxQuestionWidth, 2);
  // lines.forEach((line, index) => {
  //   ctx.fillText(line, 52, 100 + index * lineHeight);
  // });

  // // 绘制右上角日期信息
  // const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  // const createdDate = new Date(questionData.createdAt);
  // const weekDay = weekDays[createdDate.getDay()];
  // const lunar = Lunar.fromDate(createdDate);

  // // 绘制星期
  // ctx.font = '10px XiangCuiKeSong';
  // ctx.fillStyle = '#592F2F';
  // ctx.textAlign = 'right';
  // ctx.fillText(weekDay, width - 40, 84);

  // // 绘制日期数字
  // ctx.font = '30px XiangCuiKeSong';
  // ctx.fillStyle = '#CA5C21';
  // ctx.fillText(`${String(createdDate.getDate()).padStart(2, '0')}`, width - 37, 116);

  // // 绘制农历日期
  // ctx.font = '8px XiangCuiKeSong';
  // ctx.fillStyle = '#592F2F';
  // ctx.fillText(`${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}`, width - 39, 134);
  // ctx.textAlign = 'left';

  // 绘制诗句
  ctx.font = 'bold 25px XiangCuiKeSong';
  ctx.fillStyle = '#efe9d1';
  ctx.textAlign = 'center';
  const maxPoetryWidth = width - 80;
  const poetryLines = wrapText(ctx, questionData.answerPoetry, maxPoetryWidth, 3);
  poetryLines.forEach((line, index) => {
    ctx.fillText(line, width / 2, sy(212) + index * sy(35));
  });
  ctx.font = '25px XiangCuiKeSong';

  // // 绘制解析
  // ctx.font = '12px AlibabaPuHuiTi';
  // ctx.fillStyle = 'rgba(38, 0, 0, 0.5)';
  // ctx.textAlign = 'center';
  // const maxExamWidth = width - 80;
  // const examLines = wrapText(ctx, questionData.explanation || questionData.answerWord, maxExamWidth, 5);
  // examLines.forEach((line, index) => {
  //   ctx.fillText(line, width / 2, 262 + index * 20);
  // });

  await drawPosterFooter(ctx, width, height, referralCode);
};

const splitPosterText = (text: string): string[] => {
  const segments = text
    .split(/[,。，；;?？!！]/)
    .filter(Boolean)
    .flatMap(line => {
      const parts = line.split(/(=——)/);
      return parts.map(part => part.replace(/[“”"']/g, '').trim());
    })
    .filter(Boolean);

  const result: string[] = [];
  let currentSegment = '';

  for (const segment of segments) {
    if (segment.length + currentSegment.length < 10) {
      currentSegment += `${currentSegment ? ' ' : ''}${segment}`;
    } else {
      if (currentSegment) {
        result.push(currentSegment);
      }
      currentSegment = segment;
    }
  }

  if (currentSegment) {
    result.push(currentSegment);
  }

  return result;
};

const drawVerticalText = (
  ctx: CanvasRenderingContext2D,
  lines: string[],
  centerX: number,
  top: number,
  bottom: number,
  paperWidth: number,
  color = '#1e1510'
) => {
  const maxHeight = bottom - top;
  const maxColumns = Math.max(1, lines.length);
  const available = paperWidth * 0.72;
  const columnGap =
    maxColumns <= 1 ? 0 : Math.min(30, (available / Math.max(1, maxColumns - 1)) * 0.78);
  const totalColumnsWidth = (maxColumns - 1) * columnGap;
  let x = centerX + totalColumnsWidth / 2;
  const maxChars = lines.reduce((max, line) => Math.max(max, line.replace(/\s/g, '').length), 0);
  const fitLineHeight = Math.max(12, maxHeight / Math.max(1, maxChars));
  const lineHeight = Math.min(26, fitLineHeight);

  ctx.fillStyle = color;
  ctx.font = `${Math.max(12, Math.floor(lineHeight * 0.82))}px QiuHongKai`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  lines.forEach(line => {
    const chars = line.split('');
    const visibleChars = chars.filter(char => !/\s/.test(char));
    const lineBlockHeight = Math.min(maxHeight, visibleChars.length * lineHeight);
    let y = top + (maxHeight - lineBlockHeight) / 2;
    chars.forEach(char => {
      if (/\s/.test(char)) {
        return;
      }
      if (y > bottom - lineHeight) {
        return;
      }
      if (char === '《' || char === '》' || char === '—') {
        ctx.save();
        ctx.translate(x, y + lineHeight / 2);
        ctx.rotate(Math.PI / 2);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(char, 0, 0);
        ctx.restore();
      } else {
        ctx.fillText(char, x, y);
      }
      y += lineHeight;
    });
    x -= columnGap;
  });
};

export const drawRequestStateCanvas = async (
  canvasRef: HTMLCanvasElement,
  questionData: AnswerbookItem,
  backgroundUrl: string,
  isBack: boolean,
  referralCode: string
) => {
  const canvas = canvasRef;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  const dpr = window.devicePixelRatio || 1;
  const width =
    canvas.clientWidth || canvas.parentElement?.clientWidth || canvas.getBoundingClientRect().width;
  const height = width * 1.8;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  ctx.clearRect(0, 0, width, height);
  const pageBg = await loadImageWithFallback([
    getImageUrl('initial_loading_background.jpg'),
    getImageUrl('result_appear_52.png', 'sequence_frame')
  ]);
  if (pageBg) {
    drawCoverImage(ctx, pageBg, width, height);
  } else {
    ctx.fillStyle = '#2b2925';
    ctx.fillRect(0, 0, width, height);
  }

  const frameImage = await loadImageWithFallback([
    backgroundUrl,
    getImageUrl('result_appear_52.png', 'sequence_frame')
  ]);

  const paperRect = {
    x: width * 0.29,
    y: height * 0.16,
    width: width * 0.42,
    height: height * 0.68
  };

  if (frameImage) {
    drawCoverImage(ctx, frameImage, width, height);
  }

  const questionCardY = height * 0.05;
  await drawQuestionBar(ctx, questionData.questionText || '', width, questionCardY);

  const text = isBack ? questionData.answerWord : questionData.answerPoetry;
  drawVerticalText(
    ctx,
    splitPosterText(text),
    paperRect.x + paperRect.width / 2,
    paperRect.y + paperRect.height * 0.24,
    paperRect.y + paperRect.height * 0.86,
    paperRect.width,
    '#1e1510'
  );
  await drawPosterFooter(ctx, width, height, referralCode);
};
